import React, { useMemo, useState } from 'react';
import { useCurrentLocation } from 'src/components/Routes/LocationRoute';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useLoyaltyStats } from 'src/queries/loyalty';
import BusiestTimesChart from 'src/components/analytics/BusiestTimesChart';
import CumulativeStampsChart from 'src/components/analytics/CumulativeStampsChart';
import LoyaltySpreadChart from 'src/components/analytics/LoyaltySpreadChart';
import PeriodFilter from 'src/components/analytics/PeriodFilter';
import RecentRewardsTable from 'src/components/analytics/RecentRewardsTable';
import RecentStampsTable from 'src/components/analytics/RecentStampsTable';
import StampsRewardsChart from 'src/components/analytics/StampsRewardsChart';
import StatTile from 'src/components/analytics/StatTile';
import StreakProgressChart from 'src/components/analytics/StreakProgressChart';
import TopCustomersTable from 'src/components/analytics/TopCustomersTable';
import {
  FreeTrialProgress,
  NewCustomersCard,
  PaperCardsSavedCard,
  WalletSavesCard
} from 'src/components/analytics/ImpactCards';
import {
  buildSeries,
  cumulativeSeries,
  customersSeries,
  earliestEvent,
  firstStampByCustomer,
  loyaltySpread,
  streakBins,
  timeOfDaySeries,
  totalsFor
} from 'src/components/analytics/metrics';
import {
  PERIOD_OPTIONS,
  PeriodKey,
  addDays,
  bucketFor,
  formatRange,
  fromDateInput,
  percentChange,
  previousRange,
  resolveRange,
  toDateInput
} from 'src/components/analytics/period';
import { accent, bodyFont, series } from 'src/components/analytics/tokens';

/** Two column tracks that collapse to one below the lg breakpoint. */
const splitGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1fr)' },
  gap: '20px'
};

/**
 * Analytics for one location.
 *
 * Rendered inside `LocationRoute`, which supplies the heading, the location
 * guard and the tab bar — this page starts at the period control.
 *
 * `GET loyalty/stats` returns the location's whole history in one payload, so
 * changing the period is instant and costs no request. The three tables that
 * need data the payload does not carry (the scanner on each stamp, lifetime
 * per-customer totals, wallet saves) fetch for themselves.
 */
const AnalyticsPage: React.FC = () => {
  const { merchant } = useCurrentLocation();

  const [period, setPeriod] = useState<PeriodKey>('30d');
  const [customStart, setCustomStart] = useState(() => toDateInput(addDays(new Date(), -29)));
  const [customEnd, setCustomEnd] = useState(() => toDateInput(new Date()));

  // Keyed on the location, so the "is this data actually mine" check the slice
  // needed — it was persisted, and could hold the previous location's numbers
  // on a cold load — is not expressible any more. A different location is a
  // different cache entry.
  const {
    data: stats,
    isPending: showSpinner,
    isError,
  } = useLoyaltyStats(merchant.id);

  const purchases = stats?.purchases ?? [];
  const rewards = stats?.rewards ?? [];
  const streaks = stats?.streaks ?? [];

  const customError = useMemo(() => {
    if (period !== 'custom') return undefined;
    const start = fromDateInput(customStart);
    const end = fromDateInput(customEnd);
    if (!start || !end) return 'Pick both a start and an end date.';
    if (start > end) return 'The start date must come before the end date.';
    return undefined;
  }, [period, customStart, customEnd]);

  const range = useMemo(
    () =>
      resolveRange(period, {
        earliest: earliestEvent(purchases, rewards),
        customStart,
        customEnd: customError ? undefined : customEnd
      }),
    [period, purchases, rewards, customStart, customEnd, customError]
  );

  const bucket = useMemo(() => bucketFor(range), [range]);

  /** How the period reads inside a sentence: "1,204 stamps in the last 30 days". */
  const rangeLabel = useMemo(() => {
    if (period === 'all') return 'all time';
    if (period === 'custom') return formatRange(range);
    const label = PERIOD_OPTIONS.find((option) => option.key === period)?.label ?? '';
    return `the last ${label.toLowerCase()}`;
  }, [period, range]);

  /** What the headline deltas are measured against. */
  const comparison = useMemo(() => {
    if (period === 'all') return 'vs earlier period';
    if (period === 'custom') return 'vs previous period';
    const label = PERIOD_OPTIONS.find((option) => option.key === period)?.label ?? '';
    return `vs previous ${label.toLowerCase()}`;
  }, [period]);

  const firstStamps = useMemo(() => firstStampByCustomer(purchases), [purchases]);
  const totals = useMemo(
    () => totalsFor(purchases, rewards, range, firstStamps),
    [purchases, rewards, range, firstStamps]
  );
  const previous = useMemo(
    () => totalsFor(purchases, rewards, previousRange(range), firstStamps),
    [purchases, rewards, range, firstStamps]
  );

  const seriesData = useMemo(
    () => buildSeries(purchases, rewards, range, bucket),
    [purchases, rewards, range, bucket]
  );
  const cumulative = useMemo(
    () => cumulativeSeries(purchases, range, bucket),
    [purchases, range, bucket]
  );
  const customerTrend = useMemo(
    () => customersSeries(purchases, range, bucket),
    [purchases, range, bucket]
  );
  const spread = useMemo(() => loyaltySpread(purchases), [purchases]);
  const timeOfDay = useMemo(
    () => timeOfDaySeries(purchases, range, merchant.operatingHours),
    [purchases, range, merchant.operatingHours]
  );
  const streakData = useMemo(
    () => streakBins(streaks, merchant.streakTarget),
    [streaks, merchant.streakTarget]
  );

  // All time has nothing before it to compare against, so the tiles show the
  // figure alone rather than a fabricated change against an empty window.
  const changeOf = (current: number, before: number) =>
    period === 'all' ? null : percentChange(current, before);

  const uniqueCustomersAllTime = firstStamps.size;
  const isFreeTrial = merchant.subscription?.status?.toLowerCase() === 'free_trial';

  if (showSpinner) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: '80px' }}>
        <CircularProgress sx={{ color: accent.mid }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity='error' sx={{ fontFamily: bodyFont, borderRadius: '12px' }}>
        We could not load analytics for this location.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <PeriodFilter
        period={period}
        onPeriodChange={setPeriod}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStartChange={setCustomStart}
        onCustomEndChange={setCustomEnd}
        range={range}
        error={customError}
      />

      {isFreeTrial && <FreeTrialProgress stampsAllTime={purchases.length} />}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0,1fr))',
            lg: 'repeat(4, minmax(0,1fr))'
          },
          gap: '20px'
        }}
      >
        <StatTile
          label='Stamps'
          value={totals.stamps}
          color={series.stamps}
          trend={seriesData.map((point) => point.stamps)}
          hint='Stamps your staff scanned during the selected period.'
          delta={{ change: changeOf(totals.stamps, previous.stamps), basis: comparison }}
        />
        <StatTile
          label='Customers'
          value={totals.customers}
          color={accent.mid}
          trend={customerTrend}
          caption={totals.newCustomers > 0 ? `${totals.newCustomers} new` : undefined}
          hint='Distinct customers who collected at least one stamp in the period.'
          delta={{
            change: changeOf(totals.customers, previous.customers),
            basis: comparison
          }}
        />
        <StatTile
          label='Rewards issued'
          value={totals.issued}
          color={series.issued}
          trend={seriesData.map((point) => point.issued)}
          hint='Rewards customers earned by completing a card or a streak.'
          delta={{ change: changeOf(totals.issued, previous.issued), basis: comparison }}
        />
        <StatTile
          label='Rewards redeemed'
          value={totals.redeemed}
          color={series.redeemed}
          trend={seriesData.map((point) => point.redeemed)}
          caption={
            totals.issued > 0
              ? `${Math.round((totals.redeemed / totals.issued) * 100)}% of issued`
              : undefined
          }
          hint='Rewards customers actually claimed in the period, whenever they were earned.'
          delta={{
            change: changeOf(totals.redeemed, previous.redeemed),
            basis: comparison
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' },
          gap: '20px'
        }}
      >
        <NewCustomersCard count={totals.newCustomers} rangeLabel={rangeLabel} />
        <WalletSavesCard merchantId={merchant.id} />
        <PaperCardsSavedCard
          uniqueCustomers={uniqueCustomersAllTime}
          rewardsIssued={rewards.length}
          countryCode={merchant.address?.countryCode}
        />
      </Box>

      <StampsRewardsChart
        data={seriesData}
        bucket={bucket}
        rangeLabel={rangeLabel}
        locationName={merchant.name}
      />

      <Box sx={splitGrid}>
        <CumulativeStampsChart data={cumulative} bucket={bucket} rangeLabel={rangeLabel} />
        <LoyaltySpreadChart data={spread} />
      </Box>

      <Box sx={splitGrid}>
        <BusiestTimesChart
          data={timeOfDay}
          rangeLabel={rangeLabel}
          usesOperatingHours={Boolean(merchant.operatingHours?.length)}
        />
        <StreakProgressChart
          data={streakData}
          streakTarget={merchant.streakTarget}
          enabled={merchant.streaksEnabled && merchant.streakTarget > 0}
        />
      </Box>

      <TopCustomersTable merchantId={merchant.id} />

      <Box sx={splitGrid}>
        <RecentStampsTable
          merchantId={merchant.id}
          range={range}
          period={period}
          rangeLabel={rangeLabel}
        />
        <RecentRewardsTable
          rewards={rewards}
          range={range}
          rangeLabel={rangeLabel}
          locationName={merchant.name}
        />
      </Box>
    </Box>
  );
};

export default AnalyticsPage;
